import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { InputGroup, InputGroupInput, InputGroupText } from "@/components/ui/baseUi/input-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { word } from "@/modules/core/utils/i18n"
import { convertTimeObjectToTimeValue, convertTimeValueToTimeObject } from "@/modules/duration/timeConverter"
import { type FieldValueChange } from "@/typings/formik"
import { formatNumberWith2Digits, formatNumberWithXDigits } from "@modules/utils/numberUtils"
import { type FieldInputProps } from "formik"
import { useEffect, useState, type ChangeEvent } from "react"

export type HUTimeDropdownOrCustomizedTimeInputProps<K> = {
  name: string
  label?: string
  dropdownOptions: {
    label: string
    value: number
    disabled?: boolean
  }[]
  defaultValue?: number
  isRequiredInput?: boolean
  getFieldProps: (nameOrOptions: string) => FieldInputProps<number | undefined>
  setFieldValue: FieldValueChange<K>
  disabled?: boolean
  error?: React.JSX.Element
}

export function HUTimeDropdownOrCustomizedTimeInput<K>({
  name,
  label,
  dropdownOptions,
  defaultValue,
  isRequiredInput,
  getFieldProps,
  setFieldValue,
  error,
}: HUTimeDropdownOrCustomizedTimeInputProps<K>) {
  const [isCustomizableValue, setIsCustomizableValue] = useState<boolean>(false)

  useEffect(() => {
    if (getFieldProps(name).value === 0) {
      setIsCustomizableValue(true)
    } else if (
      getFieldProps(name).value !== undefined &&
      dropdownOptions.findIndex((option) => option.value === getFieldProps(name).value) < 0
    )
      setIsCustomizableValue(true)
  }, [getFieldProps(name).value, dropdownOptions])

  const value = getFieldProps(name).value
  const inputValue =
    typeof value === "number"
      ? convertTimeValueToTimeObject(value, "minutes", "days")
      : { days: 0, hours: 0, minutes: 0, seconds: 0 }

  const onDaysInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value

    if (parseInt(newValue) < 0) {
      setFieldValue(name, convertTimeObjectToTimeValue({ ...inputValue, days: 0 }, "minutes"))
    } else if (parseInt(newValue) > 999) {
      setFieldValue(
        name,
        convertTimeObjectToTimeValue(
          { ...inputValue, days: "data" in event.nativeEvent ? parseInt(event.nativeEvent.data as string) : 999 },
          "minutes",
        ),
      )
    } else {
      setFieldValue(name, convertTimeObjectToTimeValue({ ...inputValue, days: parseInt(newValue) }, "minutes"))
    }
  }

  const onHoursInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value

    if (parseInt(newValue) < 0) {
      setFieldValue(name, convertTimeObjectToTimeValue({ ...inputValue, hours: 0 }, "minutes"))
    } else if (parseInt(newValue) > 23) {
      setFieldValue(
        name,
        convertTimeObjectToTimeValue(
          { ...inputValue, hours: "data" in event.nativeEvent ? parseInt(event.nativeEvent.data as string) : 23 },
          "minutes",
        ),
      )
    } else {
      setFieldValue(name, convertTimeObjectToTimeValue({ ...inputValue, hours: parseInt(newValue) }, "minutes"))
    }
  }

  const onMinutesInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value

    if (parseInt(newValue) < 0) {
      setFieldValue(name, convertTimeObjectToTimeValue({ ...inputValue, minutes: 0 }, "minutes"))
    } else if (parseInt(newValue) > 59) {
      setFieldValue(
        name,
        convertTimeObjectToTimeValue(
          { ...inputValue, minutes: "data" in event.nativeEvent ? parseInt(event.nativeEvent.data as string) : 59 },
          "minutes",
        ),
      )
    } else {
      setFieldValue(name, convertTimeObjectToTimeValue({ ...inputValue, minutes: parseInt(newValue) }, "minutes"))
    }
  }

  return (
    <>
      {isCustomizableValue ? (
        <InputContainer label={label} isRequiredInput={isRequiredInput} error={error}>
          <InputGroup className="justify-end pr-4">
            <InputGroupInput
              placeholder="000"
              type="number"
              className="max-w-10 px-1 text-end"
              value={inputValue?.days !== undefined ? formatNumberWithXDigits(inputValue.days, 3) : ""}
              onChange={onDaysInputChange}
            />
            <InputGroupText className="mt-0.5 text-base">{word("global.day.label", { count: 1 })[0]}</InputGroupText>
            <InputGroupInput
              placeholder="00"
              type="number"
              className="max-w-8 px-1 text-end"
              value={inputValue ? formatNumberWith2Digits(inputValue.hours || 0) : ""}
              onChange={onHoursInputChange}
            />
            <InputGroupText className="mt-0.5 text-base">{word("global.hour.label", { count: 1 })[0]}</InputGroupText>
            <InputGroupInput
              placeholder="00"
              type="number"
              className="max-w-8 px-1 text-end"
              value={inputValue ? formatNumberWith2Digits(inputValue.minutes || 0) : ""}
              onChange={onMinutesInputChange}
            />
            <InputGroupText className="mt-0.5 text-base">{word("global.minute.label", { count: 1 })[0]}</InputGroupText>
          </InputGroup>
        </InputContainer>
      ) : (
        <DropdownInput
          label={label}
          options={dropdownOptions.map((option: { label: string; value: number; disabled?: boolean }) => ({
            label: option.label,
            value: option.value.toString(),
          }))}
          isRequiredInput={isRequiredInput}
          error={error}
          defaultValue={defaultValue?.toString()}
        >
          <Select
            defaultValue={value?.toString()}
            onValueChange={(value) => {
              if (value) void setFieldValue(name, parseInt(value))
            }}
            value={value?.toString()}
            items={dropdownOptions.map((option: { label: string; value: number; disabled?: boolean }) => ({
              label: option.label,
              value: option.value.toString(),
            }))}
          >
            <SelectTrigger id={name}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dropdownOptions.map((option: { label: string; value: number; disabled?: boolean }) => (
                <SelectItem key={option.value} value={option.value.toString()}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DropdownInput>
      )}
    </>
  )
}
